import React, { useEffect, useState } from "react";
import {
  LoadingContainer,
  CopyContainer,
  DivSetNewOrder,
  ButtonContainer,
  AddressContainer
} from "./styles";
import { Page } from "../../../../../components/page-base";
import CircularProgress from "@mui/material/CircularProgress";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ProductionQuantityLimitsOutlinedIcon from "@mui/icons-material/ProductionQuantityLimitsOutlined";
import { toast } from "react-toastify";
import isMobile from "is-mobile";
import { Input } from "@/components/inputs";
import { Check, Close, NavigateNextOutlined } from "@mui/icons-material";
import { Modal } from "../../../../../components/modal-base";
import { useNavigate } from "react-router-dom";
import { PaymentStep } from "@/utils/enum";
import { ICustomPlan } from "@/modules/credits/models";
import { Checkbox, FormControlLabel } from "@mui/material";
import { CreditService } from "@/modules/credits/services";
import theme from "@/styles/theme";
import { AccountsService } from "@/modules/accounts/services";

// Componentes para cada estado
export const LoadingContent: React.FC = () => (
  <LoadingContainer style={{ alignItems: "center" }}>
    <span className="lbl">Aguarde...</span>
    <CircularProgress size={"5vw"} />
  </LoadingContainer>
);

interface PaymentContentProps {
  baseImg: string;
  copyCode: string;
  customPlan: ICustomPlan | null;
  creditsDetails: {
    price: string;
    amount: number;
  };
}

export const PaymentContent: React.FC<PaymentContentProps> = ({
  baseImg,
  copyCode,
  customPlan,
  creditsDetails
}) => {
  return (
    <LoadingContainer>
      <span className="lbl">
        {isMobile() ? "Copiar o código Pix Copia e Cola" : "Escaneie o QR Code"}
      </span>
      <div>
        <img
          src={`data:image/png;base64,${baseImg}`}
          alt="QR Code"
          style={{ width: "200px", height: "200px", alignSelf: "center" }}
        />
        <CopyContainer>
          <Page.Button
            label="Copiar código"
            onClick={() => {
              toast.success("Código copiado para a área de transferência!");
              navigator.clipboard.writeText(copyCode);
            }}
          />
          <p>{copyCode}</p>
        </CopyContainer>
      </div>
      <div className="details-container flex">
        <div className="item">
          <PersonSearchOutlinedIcon
            sx={{
              fontSize: "14px",
              color: "#a0522d" // Exemplo de cor; ajustar conforme theme
            }}
          />
          <span>
            {!!customPlan || creditsDetails.amount === 75
              ? `${creditsDetails.amount} consultas`
              : creditsDetails.amount > 1
              ? `${creditsDetails.amount} consultas`
              : "01 consulta"}
          </span>
        </div>
        <div className="item">
          <LocalOfferOutlinedIcon
            sx={{
              fontSize: 16,
              color: "#a0522d"
            }}
          />
          <span>
            R$ {Number(creditsDetails.price) * creditsDetails.amount},00
          </span>
        </div>
      </div>
      <div className="bottom">
        <GppGoodOutlinedIcon />
        Ambiente seguro
      </div>
    </LoadingContainer>
  );
};

export const SuccessContent: React.FC<{
  toggleModal: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<PaymentStep>>;
  paymentId: string;
  paymentValue: number;
  selectedAccount: number;
}> = ({
  toggleModal,
  setCurrentStep,
  paymentId,
  paymentValue,
  selectedAccount
}) => {
  const navigate = useNavigate();
  const [emitirNFE, setEmitirNFE] = useState<boolean>(false);

  const verifyAddress = async () => {
    const response = await AccountsService.verifyAddress(selectedAccount);

    if (response.success) {
      return response.addressFilled;
    } else {
      toast.error(response.message);
      return false;
    }
  };

  const handleCheckboxChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    setEmitirNFE(checked);
  };

  const proceed = async () => {
    setCurrentStep(PaymentStep.LOADING);
    if (!emitirNFE) navigate("/consult");

    const hasAddress = await verifyAddress();
    if (!hasAddress) {
      setCurrentStep(PaymentStep.ADDRESS);
      return;
    }

    await emmitNFE(paymentId).then(() => {
      toggleModal();
      navigate("/consult");
    });
  };

  const emmitNFE = async (paymentId: string) => {
    if (!paymentId) {
      toast.error("Erro ao gerar NFE");
      return;
    }
    const response = await CreditService.generateNFE(
      paymentId,
      paymentValue,
      selectedAccount
    );
    if (response.success) {
      toast.success("NFE gerada com sucesso, verifique seu email.");
    } else {
      toast.error(response.message);
    }
  };

  return (
    <LoadingContainer style={{ alignItems: "center" }}>
      <span className="lbl">Sucesso!</span>
      <VerifiedUserOutlinedIcon
        sx={{
          width: "12vw",
          color: "green",
          height: "15vh"
        }}
      />
      <div className="bottom">Compra concluída!</div>
      <FormControlLabel
        control={
          <Checkbox
            checked={emitirNFE}
            onChange={handleCheckboxChange}
            sx={{ color: "#2C2C2C" }}
          />
        }
        label="Emitir NFE"
      />
      <ButtonContainer>
        <Modal.Button
          onClick={proceed}
          startIcon={<Check />}
          bgColor="#2C2C2C"
          color="white"
        >
          Continuar
        </Modal.Button>
      </ButtonContainer>
    </LoadingContainer>
  );
};

export const ErrorContent: React.FC<{
  toggleModal: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<PaymentStep>>;
  handleTryAgain: () => void;
}> = ({ toggleModal, setCurrentStep, handleTryAgain }) => (
  <LoadingContainer style={{ alignItems: "center" }}>
    <span className="lbl">Problemas com o pagamento</span>
    <ProductionQuantityLimitsOutlinedIcon
      sx={{
        width: "12vw",
        color: "red",
        height: "15vh"
      }}
    />
    <div className="bottom">Erro ao realizar pagamento.</div>
    <ButtonContainer>
      <Modal.Button
        onClick={() => {
          toggleModal();
          setCurrentStep(PaymentStep.LOADING);
        }}
        startIcon={<Close />}
        bgColor="#FDF8F0"
        color="black"
      >
        Cancelar
      </Modal.Button>
      <Modal.Button
        onClick={handleTryAgain}
        startIcon={<NavigateNextOutlined />}
        bgColor="#2C2C2C"
        color="white"
      >
        Tentar novamente
      </Modal.Button>
    </ButtonContainer>
  </LoadingContainer>
);
export const ConsultAmountInput: React.FC<{
  minOrder: number;
  creditsDetails: {
    price: string;
    amount: number;
  };
  confirmPurchase: (amount: number, price: string) => Promise<void>;
}> = ({ minOrder, confirmPurchase, creditsDetails }) => {
  const [amount, setAmount] = useState(creditsDetails.amount.toString());

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    confirmPurchase(Number(amount), Number(creditsDetails.price).toString());
  };

  return (
    <DivSetNewOrder>
      <form className="w-full" onSubmit={handleFormSubmit}>
        <Input.Label
          label="Número de consultas"
          placeholder="Quantidade de consultas"
          setValue={setAmount}
          value={amount}
          type="number"
          min={minOrder}
        />
        <div className="mb-4 text-zinc-600">
          <span>
            Total de R$ {Number(creditsDetails.price) * Number(amount)},00
          </span>
        </div>
        <Page.Button
          label="COMPRAR"
          type="submit"
          disabled={Number(amount) < minOrder}
        />
      </form>
    </DivSetNewOrder>
  );
};

export const AddressContent: React.FC<{
  toggleModal: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<PaymentStep>>;
  paymentId: string;
  paymentValue: number;
  selectedAccount: number;
}> = ({
  toggleModal,
  setCurrentStep,
  selectedAccount,
  paymentId,
  paymentValue
}) => {
  const windowHeight = window.innerHeight;

  const [street, setStreet] = useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");
  const [isZipCodeValid, setIsZipCodeValid] = useState<boolean>(false);
  const [streetNumber, setStreetNumber] = useState<string>("");
  const [complement, setComplement] = useState<string>("");
  const [neighborhood, setNeighborhood] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");

  const fetchZipCode = async (zipCode: string) => {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json`);
      if (!response.ok) {
        throw new Error("Erro na requisição: " + response.status);
      }
      const data = await response.json();
      if (data.erro) {
        return {
          success: false,
          message: "CEP não encontrado"
        };
      }
      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        message: "Erro ao buscar o CEP"
      };
    }
  };

  const handleCloseModal = () => {
    toggleModal();
    setCurrentStep(PaymentStep.PAYMENT);
  };

  const handleSubmitAddress = async () => {
    try {
      const { success, message } = await AccountsService.updateAddress(
        selectedAccount,
        zipCode,
        street,
        streetNumber,
        complement,
        neighborhood,
        city,
        state
      );
      if (!success) {
        toast.error(message || "Erro ao cadastrar endereço");
        return;
      }

      toast.success("Endereço cadastrado com sucesso!");
      await new Promise((resolve) => setTimeout(resolve, 250));
      
      const nfeOk = await emmitNFE(paymentId);
      if (nfeOk) {
        handleCloseModal();
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Erro ao processar o endereço");
    }
  };

  useEffect(() => {
    const digits = zipCode.replace(/\D/g, "");
    if (digits.length === 8) {
      (async () => {
        const res = await fetchZipCode(digits);
        if (res.success) {
          const { data } = res;
          setStreet(data.logradouro || "");
          setNeighborhood(data.bairro || "");
          setCity(data.localidade || "");
          setState(data.uf || "");
          setIsZipCodeValid(true);
        } else {
          setIsZipCodeValid(false);
          toast.error("CEP inválido ou não encontrado.");
        }
      })();
    }
  }, [zipCode]);

  const emmitNFE = async (paymentId: string) => {
    if (!paymentId) {
      toast.error("Erro ao gerar NFE");
      return false;
    }
    const response = await CreditService.generateNFE(
      paymentId,
      paymentValue,
      selectedAccount
    );
    if (response.success) {
      toast.success("NFE gerada com sucesso, verifique seu email.");
      return true;
    } else {
      toast.error(response.message);
      return false;
    }
  };
  return (
    <AddressContainer>
      <span className="lbl">Cadastre seu endereço</span>
      <div
        className="form-container"
        id="form-address"
        style={{
          height: windowHeight < 750 ? "460px" : "inherit",
          overflowY: windowHeight < 750 ? "auto" : "hidden"
        }}
      >
        <div className="row-address">
          <Input.Masked
            label="CEP"
            setValue={setZipCode}
            type="CEP"
            setIsValid={setIsZipCodeValid}
            isValid={isZipCodeValid}
          />
        </div>

        <div className="row-address">
          <Input.Label
            label="Logradouro"
            placeholder="Digite seu logradouro"
            setValue={setStreet}
            value={street}
            type="text"
          />
          <Input.Label
            label="Número"
            placeholder="Digite o número"
            setValue={setStreetNumber}
            value={streetNumber}
            type="text"
          />
        </div>

        <div className="row-address">
          <Input.Label
            label="Complemento"
            placeholder="Digite o complemento"
            setValue={setComplement}
            value={complement}
            type="text"
          />
          <Input.Label
            label="Bairro"
            placeholder="Digite o bairro"
            setValue={setNeighborhood}
            value={neighborhood}
            type="text"
          />
        </div>
        <div className="row-address">
          <Input.Label
            label="Cidade"
            placeholder="Digite a cidade"
            setValue={setCity}
            value={city}
            type="text"
          />
          <Input.Label
            label="Estado"
            placeholder="Digite seu estado"
            setValue={setState}
            value={state}
            type="text"
          />
        </div>
      </div>
      <ButtonContainer>
        <Modal.Button
          onClick={handleSubmitAddress}
          startIcon={<Check />}
          bgColor={theme.colors.palete.green}
          color="white"
          disabled={
            !zipCode ||
            !street ||
            !streetNumber ||
            !neighborhood ||
            !city ||
            !state ||
            !isZipCodeValid
          }
        >
          Cadastrar
        </Modal.Button>
        <Modal.Button
          onClick={handleCloseModal}
          startIcon={<Close />}
          bgColor={theme.colors.palete.red}
          color="white"
        >
          Fechar
        </Modal.Button>
      </ButtonContainer>
    </AddressContainer>
  );
};
